// workerPool.js
// import WorkerScript from '@/workers/cacheWorker.js?worker';

export class WorkerPool {
  constructor(workerUrl, maxWorkers = 6) {
    this.maxWorkers = maxWorkers;
    this.workerQueue = [];
    this.activeWorkers = 0;
    this.workerPool = [];
    this.listeners = [];
    this.workerStatus = new Map(); // Track busy workers

    this.createWorkers();
  }

  createWorkers() {
    for (let i = 0; i < this.maxWorkers; i++) {
      const worker = new Worker(new URL('./cacheWorker.js', import.meta.url), {
        type: 'module',
      });
      this.workerStatus.set(worker, false); // false = available

      worker.onmessage = (event) => {
        if (event.data.error) {
          console.error('Worker error:', event.data.error.message);
        }
        this.releaseWorker(worker);

        this.notifyListeners({
          type: 'result',
          data: event.data,
        });

        this.processNext();
      };

      worker.onerror = (errorEvent) => this.handleWorkerError(errorEvent.error || errorEvent, worker);

      this.workerPool.push(worker);
    }
  }

  // Release exactly once per task; releasing twice lets a worker be double-booked and the busy count drift.
  releaseWorker(worker) {
    if (this.workerStatus.get(worker)) {
      this.workerStatus.set(worker, false);
      this.activeWorkers = Math.max(0, this.activeWorkers - 1);
    }
  }

  processNext() {
    if (this.workerQueue.length > 0 && this.getAvailableWorker()) {
      this.assignTask(this.workerQueue.shift());
    }
  }

  addTask(taskData) {
    if (this.activeWorkers < this.maxWorkers) {
      this.assignTask(taskData);
    } else {
      // console.log('Queueing task', taskData);
      this.workerQueue.push(taskData);
    }
  }

  assignTask(taskData) {
    const worker = this.getAvailableWorker();
    if (worker) {
      this.activeWorkers++;
      this.workerStatus.set(worker, true); // Mark worker as busy

      // Notify that the worker has started processing
      this.notifyListeners({
        type: 'started',
        worker,
        data: taskData,
      });

      // console.log('Assigning task:', taskData);
      worker.postMessage(taskData);
    } else {
      console.warn('No available worker, adding to queue');
      this.workerQueue.push(taskData);
    }
  }

  getAvailableWorker() {
    return [...this.workerStatus.keys()].find((worker) => !this.workerStatus.get(worker));
  }

  notifyListeners(data) {
    // console.log('notifyListeners');
    this.listeners.forEach((listener) => listener(data));
  }

  onmessage(callback) {
    // console.log('onmessage');
    this.listeners.push(callback);
  }

  terminate() {
    this.workerPool.forEach((worker) => worker.terminate());
  }

  clearQueue() {
    // console.log('Clearing pending tasks');
    this.workerQueue = [];
  }

  getQueue() {
    return this.workerQueue;
  }

  handleWorkerError(error, worker) {
    console.error('Worker error:', error?.message || error);
    this.releaseWorker(worker);
    this.processNext();
  }

  restartWorkers() {
    this.workerPool.forEach((worker) => worker.terminate());

    this.workerPool = [];
    this.workerStatus.clear();
    this.workerQueue = [];
    this.activeWorkers = 0;

    this.createWorkers();
  }
}
